import type { ExpAdmission, ExpExactEvidence, GameStateSnapshot } from '@/domain/types'
import { expRequirementRange, intersectExpRequirementRanges, representativeExpRequirement, type ExpRequirementRange } from './exp-requirement-estimator'
import { EXP_TRANSITION_REVIEW_MS, freshLevel, lossMatchesLevel, sameExpDigits } from './exp-transition'

type Risk = NonNullable<ExpAdmission['reason']>
interface Interval { readonly at: number; readonly delta: number; readonly seconds: number }
interface Pending {
  first: ExpExactEvidence
  last: ExpExactEvidence
  range: ExpRequirementRange | null
  reason: Risk
  count: number
  credit: number
  threshold: number
}

export interface ExpAdmissionCheckpoint {
  readonly anchor: ExpExactEvidence | null
  readonly ranges: readonly (readonly [number, ExpRequirementRange])[]
  readonly intervals: readonly Interval[]
  readonly reference: { readonly delta: number; readonly rate: number } | null
  readonly total: number
  readonly epoch: number
  readonly epochStartTotal: number
  readonly epochStartAt: number
  readonly rejectedGap: { readonly level: number; readonly threshold: number } | null
}

/** Admission controls both endpoint adoption and earnings; repeated OCR never authorizes a rejected gap. */
export class ExpAdmissionFilter {
  private anchor: ExpExactEvidence | null = null
  private ranges = new Map<number, ExpRequirementRange>()
  private decisions = new Map<number, ExpAdmission>()
  private sources = new Map<number, ExpExactEvidence>()
  private latestSourceAt = -Infinity
  private pending: Pending | null = null
  private intervals: Interval[] = []
  private reference: ExpAdmissionCheckpoint['reference'] = null
  private recovering = false
  private total = 0
  private epoch = 0
  private epochStartTotal = 0
  private epochStartAt = 0
  private rejectedGap: ExpAdmissionCheckpoint['rejectedGap'] = null
  private levelEvidence: { level: number; sourceAt: number } | null = null
  private recentLoss: { before: ExpExactEvidence; first: ExpExactEvidence; last: ExpExactEvidence;
    totalBefore: number; change: NonNullable<ExpAdmission['change']> } | null = null
  private rejectedBaseline: { before: ExpExactEvidence; total: number; at: number } | null = null

  reset(state: GameStateSnapshot | null = null): void {
    this.anchor = state?.level !== null && state?.level !== undefined && state.exp.absolute !== null
      && state.exp.ratio !== null && !state.exp.estimated && expRequirementRange(state.exp, state.exp.percentagePrecision) !== undefined
      ? { level: state.level, value: state.exp, confidence: state.confidence.exp, sourceAt: state.observedAt?.exp ?? state.capturedAt,
          levelSourceAt: state.observedAt?.level ?? state.capturedAt,
          deliveredAt: state.capturedAt, percentagePrecision: state.exp.percentagePrecision, confirmation: 'consensus' }
      : null
    this.ranges.clear()
    if (this.anchor) {
      const range = state!.exp.requiredExpRange ?? expRequirementRange(state!.exp, state!.exp.percentagePrecision)
      if (range) this.ranges.set(this.anchor.level!, range)
    }
    this.intervals = []
    this.reference = null
    this.total = 0
    this.epoch = 0
    this.epochStartTotal = 0
    this.epochStartAt = this.anchor?.sourceAt ?? 0
    this.rejectedGap = null
    this.breakContinuity()
    this.recovering = false
  }

  checkpoint(): ExpAdmissionCheckpoint {
    return { anchor: this.anchor, ranges: [...this.ranges], intervals: [...this.intervals],
      reference: this.reference, total: this.total, epoch: this.epoch,
      epochStartTotal: this.epochStartTotal, epochStartAt: this.epochStartAt, rejectedGap: this.rejectedGap }
  }

  restore(checkpoint: ExpAdmissionCheckpoint): void {
    this.anchor = checkpoint.anchor
    this.ranges = new Map(checkpoint.ranges)
    this.intervals = [...checkpoint.intervals]
    this.reference = checkpoint.reference
    this.total = checkpoint.total
    this.epoch = checkpoint.epoch
    this.epochStartTotal = checkpoint.epochStartTotal
    this.epochStartAt = checkpoint.epochStartAt
    this.rejectedGap = checkpoint.rejectedGap
    this.breakContinuity()
  }

  breakContinuity(): void {
    this.pending = null
    this.decisions.clear()
    this.sources.clear()
    this.latestSourceAt = this.anchor?.sourceAt ?? -Infinity
    this.recovering = this.anchor !== null
    this.levelEvidence = null
    this.recentLoss = null
    this.rejectedBaseline = null
  }

  observeLevel(level: number, sourceAt: number, deliveredAt: number): void {
    if (!Number.isInteger(level) || level < 1 || level > 200 || !Number.isFinite(sourceAt)
      || sourceAt > deliveredAt || sourceAt <= (this.levelEvidence?.sourceAt ?? -Infinity)) return
    this.levelEvidence = { level, sourceAt }
  }

  /** Revisit only an uncommitted endpoint; a level refresh is not another EXP sample. */
  resolvePending(deliveredAt: number, minimumConfidence = .62): { evidence: ExpExactEvidence; admission: ExpAdmission } | null {
    const pending = this.pending
    const level = this.levelEvidence
    if (!pending || !level || !this.anchor || deliveredAt - pending.first.sourceAt > EXP_TRANSITION_REVIEW_MS
      || level.sourceAt < pending.first.sourceAt
      || pending.last.value.absolute! >= this.anchor.value.absolute! && pending.reason !== 'requirement-conflict') return null
    if (level.level !== pending.last.level && level.level !== this.anchor.level! + 1) return null
    const evidence = { ...pending.last, level: level.level, levelSourceAt: level.sourceAt, deliveredAt }
    const admission = this.observe(evidence, minimumConfidence)
    return admission.status === 'accepted' ? { evidence: { ...evidence, value: admission.value! }, admission } : null
  }

  isPending(): boolean { return this.pending !== null || this.recovering }

  permitsEstimate(level: number | null, ratio: number | null, sourceAt: number): boolean {
    if (!this.anchor || level === null || ratio === null || this.isPending()) return false
    const range = this.ranges.get(level)
    if (!range) return false
    if (range.maximum === Number.MAX_SAFE_INTEGER) return false
    const required = representativeExpRequirement(range)
    const sample: ExpExactEvidence = { level, sourceAt, deliveredAt: sourceAt, confidence: 1, confirmation: 'source-frame',
      value: { absolute: Math.round(required * ratio), ratio, requiredExp: required } }
    if (level === this.anchor.level && ratio < this.anchor.value.ratio! - .0005) return false
    const delta = this.difference(this.anchor, sample)
    return delta !== null && delta <= this.threshold(this.anchor, sample)
  }

  observe(sample: ExpExactEvidence, minimumConfidence = .62): ExpAdmission {
    const decision = this.observeSample(sample, minimumConfidence)
    return { ...decision, displayStatus: decision.status }
  }

  private observeSample(sample: ExpExactEvidence, minimumConfidence: number): ExpAdmission {
    const range = expRequirementRange(sample.value, sample.percentagePrecision)
    if (!Number.isFinite(sample.confidence) || sample.confidence < minimumConfidence || sample.level === null || !Number.isInteger(sample.level)
      || sample.level < 1 || sample.level > 200 || sample.value.estimated || range === undefined
      || !Number.isFinite(sample.sourceAt) || !Number.isFinite(sample.deliveredAt) || sample.sourceAt > sample.deliveredAt) {
      return { status: 'rejected', reason: 'invalid', samples: 0, creditedDelta: 0 }
    }
    if (sample.levelSourceAt != null) this.observeLevel(sample.level, sample.levelSourceAt, sample.deliveredAt)
    if (this.anchor && sample.level === this.anchor.level! + 1 && sample.levelSourceAt != null
      && sample.sourceAt < sample.levelSourceAt && sample.value.absolute! >= this.anchor.value.absolute!
      && sample.value.absolute! > 0 && sample.value.ratio! > 0
      && lossMatchesLevel(this.anchor.value, { ...sample.value, percentagePrecision: sample.percentagePrecision })) {
      sample = { ...sample, level: this.anchor.level, levelSourceAt: this.anchor.levelSourceAt }
    }
    if (this.recentLoss && sample.deliveredAt - this.recentLoss.first.sourceAt > EXP_TRANSITION_REVIEW_MS) this.recentLoss = null
    const cached = this.decisions.get(sample.sourceAt)
    const source = this.sources.get(sample.sourceAt)
    if (cached && source) {
      if (!sameExpDigits(source, sample)) return { status: 'rejected', reason: 'stale', samples: 0, creditedDelta: 0 }
      const canComplete = cached.status === 'pending' && this.pending?.last.sourceAt === sample.sourceAt
        && freshLevel(this.levelEvidence, sample.level!, this.pending.first.sourceAt)
        && sample.deliveredAt - this.pending.first.sourceAt <= EXP_TRANSITION_REVIEW_MS
        && (sample.level === source.level || this.anchor !== null && sample.level === this.anchor.level! + 1)
      const canCorrect = this.canCorrectLoss(sample)
      if (!canComplete && !canCorrect) return source.level === sample.level
        ? cached : { status: 'rejected', reason: 'stale', samples: 0, creditedDelta: 0 }
      const decision = canCorrect ? this.correctLoss(sample, range) : this.decide(sample, range, true)
      this.decisions.set(sample.sourceAt, decision)
      this.sources.set(sample.sourceAt, sample)
      return decision
    }
    if (sample.sourceAt <= this.latestSourceAt) return { status: 'rejected', reason: 'stale', samples: 0, creditedDelta: 0 }
    if (sample.sourceAt - this.latestSourceAt > 120_000 && this.anchor) this.breakContinuity()
    this.latestSourceAt = sample.sourceAt
    const decision = this.canCorrectLoss(sample) ? this.correctLoss(sample, range) : this.decide(sample, range)
    this.decisions.set(sample.sourceAt, decision)
    this.sources.set(sample.sourceAt, sample)
    for (const at of this.decisions.keys()) {
      if (at < sample.sourceAt - 180_000 || this.decisions.size > 512) {
        this.decisions.delete(at)
        this.sources.delete(at)
      }
    }
    return decision
  }

  private decide(sample: ExpExactEvidence, range: ExpRequirementRange | null, refresh = false): ExpAdmission {
    const anchor = this.anchor
    const rejected = this.rejectedBaseline
    if (rejected && sample.deliveredAt - rejected.at > EXP_TRANSITION_REVIEW_MS) this.rejectedBaseline = null
    else if (rejected && anchor && sample.level === rejected.before.level
      && (sample.level !== anchor.level || sample.value.absolute! < anchor.value.absolute!)
      && lossMatchesLevel(rejected.before.value, { ...sample.value, percentagePrecision: sample.percentagePrecision })) {
      const delta = this.difference(rejected.before, sample)
      if (delta !== null && delta >= 0 && delta <= this.threshold(rejected.before, sample)) {
        const credit = Math.max(0, rejected.total + delta - this.total)
        this.epoch += 1
        this.epochStartAt = sample.sourceAt
        this.epochStartTotal = this.total
        this.pending = null
        this.rejectedBaseline = null
        return { ...this.accept(sample, range, credit, 'rebaseline', 0, false),
          change: { id: String(sample.sourceAt), revision: 1, fromSourceAt: sample.sourceAt, status: 'discarded', kind: 'correction' } }
      }
    }
    const known = this.ranges.get(sample.level!)
    const merged = known && range ? intersectExpRequirementRanges([known, range]) : range ?? known ?? null
    if (!anchor) return this.accept(sample, range, 0, 'baseline', 0)
    const delta = this.difference(anchor, sample)
    const limit = this.rejectedGap?.level === sample.level
      ? Math.min(this.threshold(anchor, sample), this.rejectedGap.threshold) : this.threshold(anchor, sample)
    // Impossible same-level requirements must not freeze the gain threshold
    // either: normal growth during a long OCR outage still uses elapsed time.
    if (merged !== undefined && delta !== null && delta > limit) this.rejectedGap = { level: sample.level!, threshold: limit }
    let reason: Risk | undefined
    if (known && range && !merged) reason = 'requirement-conflict'
    else if (sample.level !== anchor.level && delta === null) reason = 'level-correction'
    else if (delta !== null && delta < 0) reason = 'decrease'
    else if (delta === null || delta > limit) reason = 'large-gain'
    else if (this.recovering) reason = 'recovery'

    if (!reason) {
      const pending = this.pending
      this.pending = null
      const result = this.accept(sample, merged ?? null, delta!, sample.level === anchor.level ? 'continuous' : 'level-up', limit)
      return pending ? { ...result, change: { id: String(pending.first.sourceAt), revision: 1,
        fromSourceAt: pending.first.sourceAt, status: sample.level === anchor.level ? 'discarded' : 'confirmed',
        kind: sample.level === anchor.level ? 'correction' : 'level-up' } } : result
    }

    const previous = this.pending
    const intersection = previous?.range && range ? intersectExpRequirementRanges([previous.range, range]) : range ?? previous?.range ?? null
    const step = previous ? sample.value.absolute! - previous.last.value.absolute! : 0
    // Review growth is subject to the same bound, using the pre-review requirement and rate reference.
    const stepLimit = previous ? this.threshold({ ...anchor, sourceAt: previous.last.sourceAt }, sample) : limit
    const compatible = previous && previous.reason === reason && previous.last.level === sample.level
      && sample.sourceAt > previous.last.sourceAt && sample.sourceAt - previous.last.sourceAt <= 120_000
      && step >= 0 && step <= stepLimit && intersection !== undefined
    const pending: Pending = refresh && previous && previous.last.level === sample.level ? { ...previous, last: sample } : compatible
      ? { ...previous, last: sample, range: intersection ?? null, count: previous.count + 1, credit: previous.credit + step }
      : { first: sample, last: sample, range, reason, count: 1, credit: 0,
          threshold: reason === 'large-gain' && previous?.reason === reason && previous.last.level === sample.level ? previous.threshold : limit }
    this.pending = pending
    const decrease = sample.level === anchor.level && sample.value.absolute! < anchor.value.absolute!
    const confirmedLoss = decrease && pending.count >= 3
      && freshLevel(this.levelEvidence, sample.level!, pending.first.sourceAt)
      && lossMatchesLevel(anchor.value, { ...sample.value, percentagePrecision: sample.percentagePrecision })
      && reason !== 'requirement-conflict'
    const needsFreshLevel = (decrease || reason === 'requirement-conflict')
      && !freshLevel(this.levelEvidence, sample.level!, pending.first.sourceAt)
    // The requirement cannot change inside the same level. Repeated malformed
    // digits are not evidence for replacing the last trustworthy requirement.
    const conflictsWithCurrentLevel = reason === 'requirement-conflict' && sample.level === anchor.level
    if (pending.count < 3 || decrease && !confirmedLoss || needsFreshLevel || conflictsWithCurrentLevel) return {
      status: 'pending', reason, samples: pending.count, creditedDelta: 0, threshold: pending.threshold,
      ...(decrease || needsFreshLevel ? { change: { id: String(pending.first.sourceAt), revision: 0,
        fromSourceAt: pending.first.sourceAt, status: 'pending' as const } } : {}),
    }

    const recoveryCredit = reason === 'recovery' && delta !== null && delta >= 0 && delta <= pending.threshold
    const transition = recoveryCredit ? sample.level === anchor.level ? 'continuous' : 'level-up' : 'rebaseline'
    const credit = recoveryCredit ? delta : pending.credit
    if (!recoveryCredit) {
      this.epoch += 1
      this.epochStartTotal = this.total
      this.epochStartAt = pending.first.sourceAt
    }
    this.pending = null
    this.recovering = false
    const confirmedRange = reason === 'requirement-conflict' ? pending.range
      : merged && pending.range ? intersectExpRequirementRanges([merged, pending.range])! : merged ?? pending.range
    const totalBefore = this.total
    if ((reason === 'large-gain' || reason === 'requirement-conflict') && sample.value.absolute! > anchor.value.absolute!
      || reason === 'level-correction') {
      this.rejectedBaseline ??= { before: anchor, total: totalBefore, at: pending.first.sourceAt }
    }
    const result = this.accept(sample, confirmedRange, credit, transition, pending.threshold, recoveryCredit)
    const change = confirmedLoss ? { id: String(pending.first.sourceAt), revision: 1, fromSourceAt: pending.first.sourceAt,
      status: 'confirmed' as const, kind: 'loss' as const } : undefined
    if (change) this.recentLoss = { before: anchor, first: pending.first, last: sample, totalBefore, change }
    return { ...result, samples: 3, reason, displayBaseline: pending.first,
      confirmedLoss, ...(change ? { change } : {}),
      ...(!recoveryCredit ? { rebaseline: pending.first } : {}) }
  }

  private canCorrectLoss(sample: ExpExactEvidence): boolean {
    const loss = this.recentLoss
    if (!loss || sample.level !== loss.before.level! + 1
      || !freshLevel(this.levelEvidence, sample.level!, loss.first.sourceAt)
      || sample.deliveredAt - loss.first.sourceAt > EXP_TRANSITION_REVIEW_MS) return false
    // A second decrease is a new event (e.g. real death followed by an actual upgrade).
    if (this.anchor && sample.value.absolute! < this.anchor.value.absolute!) return false
    return sample.sourceAt === this.anchor?.sourceAt && sameExpDigits(sample, this.anchor) && sameExpDigits(this.anchor, loss.last)
      || loss.first.value.absolute === 0 && loss.last.value.absolute === 0
        && !lossMatchesLevel(loss.before.value, { ...sample.value, percentagePrecision: sample.percentagePrecision })
  }

  private correctLoss(sample: ExpExactEvidence, range: ExpRequirementRange | null): ExpAdmission {
    const loss = this.recentLoss!
    const delta = this.difference(loss.before, sample)
    if (delta === null) return { status: 'rejected', reason: 'invalid', samples: 0, creditedDelta: 0 }
    const credit = Math.max(0, loss.totalBefore + delta - this.total)
    const limit = this.threshold(loss.before, sample)
    this.recentLoss = null
    this.pending = null
    const result = this.accept(sample, range, delta <= limit ? credit : 0, 'level-up', limit, false)
    return { ...result, change: { ...loss.change, revision: loss.change.revision + 1, kind: 'level-up' } }
  }

  private accept(sample: ExpExactEvidence, range: ExpRequirementRange | null, credit: number,
    transition: NonNullable<ExpAdmission['transition']>, threshold: number, learn = true): ExpAdmission {
    if (range) this.ranges.set(sample.level!, range)
    const required = range && range.maximum !== Number.MAX_SAFE_INTEGER ? representativeExpRequirement(range, sample.value.absolute!, sample.value.requiredExp
      ?? (sample.value.ratio ? sample.value.absolute! / sample.value.ratio : null)) : null
    const value = { ...sample.value, requiredExp: required, requiredExpApproximate: required !== null,
      requiredExpRange: range, percentagePrecision: sample.percentagePrecision ?? 2 }
    if (learn && this.anchor && credit > 0) {
      const seconds = (sample.sourceAt - this.anchor.sourceAt) / 1000
      if (seconds > 0) this.intervals.push({ at: sample.sourceAt, delta: credit, seconds })
    }
    this.intervals = this.intervals.filter(entry => sample.sourceAt - entry.at <= 120_000).slice(-20)
    if (this.intervals.length) this.reference = {
      delta: median(this.intervals.map(entry => entry.delta)),
      rate: median(this.intervals.map(entry => entry.delta / entry.seconds)),
    }
    this.total += credit
    this.rejectedGap = null
    this.anchor = { ...sample, value }
    return { status: 'accepted', samples: 1, value, level: sample.level, creditedDelta: credit, transition, threshold,
      accounting: { total: this.total, epoch: this.epoch, sourceAt: sample.sourceAt,
        epochStartTotal: this.epochStartTotal, epochStartAt: this.epochStartAt } }
  }

  private threshold(anchor: ExpExactEvidence, sample: ExpExactEvidence): number {
    const seconds = Math.max(0, (sample.sourceAt - anchor.sourceAt) / 1000)
    const recent = this.intervals.filter(entry => sample.sourceAt - entry.at <= 120_000)
    const reference = recent.length ? {
      delta: median(recent.map(entry => entry.delta)),
      rate: median(recent.map(entry => entry.delta / entry.seconds)),
    } : this.reference
    if (reference) return Math.max(reference.delta * 8, reference.rate * seconds * 8)
    const currentRange = expRequirementRange(sample.value, sample.percentagePrecision)
    const required = anchor.value.requiredExp ?? this.ranges.get(anchor.level!)?.minimum
      ?? (currentRange && currentRange.maximum !== Number.MAX_SAFE_INTEGER ? representativeExpRequirement(currentRange) : 0)
    return Math.min(required * .05 * Math.max(seconds, 1), required * .2)
  }

  private difference(start: ExpExactEvidence, end: ExpExactEvidence): number | null {
    if (end.level === start.level) return end.value.absolute! - start.value.absolute!
    const wrapped = end.level === start.level! + 1
      && (end.value.absolute! < start.value.absolute! || end.value.ratio! < start.value.ratio! - .0002)
    const required = start.value.requiredExp
    return wrapped && required != null && required >= start.value.absolute!
      ? required - start.value.absolute! + end.value.absolute! : null
  }
}

function median(values: number[]): number {
  values.sort((a, b) => a - b)
  const middle = Math.floor(values.length / 2)
  return values.length % 2 ? values[middle]! : (values[middle - 1]! + values[middle]!) / 2
}
